import React, {useContext, useRef, useState} from 'react';
import { useTheme } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

import SystemContext from "../../../SystemContext";
import DataGrid from "../../../controls/DataGrid";
import { MaskedNumber } from '../../../controls';

const ActualFlownValues = (props) => {
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const dialogTitle = "Actual Flown Values for " + props.awb;

  const columns = [];
  const rows = [];

  const getVolumetricWeight = (volume) => {
    return ( volume / 0.006);
  }

  const getChargeableWeight = (weight, volume) => {
    if (volume) {
      const volumetricWeight = getVolumetricWeight(volume);
      if (!weight)
        return volumetricWeight;
      return (volumetricWeight > weight ? volumetricWeight : weight);
    }
    return 0;
  }

  const handleCellValueChanged = (params) => {
    const {node, column, newValue, oldValue} = params;
    if (newValue !== oldValue) {
      node.data[column?.colId] = node.rowIndex === 0 ? Math.floor(newValue) : newValue;
      node.data["diff"] = node.data[column?.colId] - node.data["booked"];

      let model;
      let visibleRows = [];
      const activeApi = params.api ? params.api : null;
      if(activeApi){
        model = activeApi.getModel();
        visibleRows = model.rowsToDisplay;
      }

      const bookingCW = getChargeableWeight(visibleRows[1].data?.booked, visibleRows[2].data?.booked);
      const actualCW = getChargeableWeight(visibleRows[1].data?.actual ? visibleRows[1].data?.actual : visibleRows[1].data?.booked,
        visibleRows[2].data?.actual ? visibleRows[2].data?.actual : visibleRows[2].data?.booked);

      visibleRows[3].data.actual = actualCW;
      visibleRows[3].data.diff = actualCW - visibleRows[3].data.booked;
    }
  }

  const numericColumnEditor = {
    component: "MaskedNumber",
    params: {
      isdatagrid: true,
      decimalScale: 2,
      fixedDecimalScale: true,
      style: {width:"100%", fontSize: "0.875rem", textAlign: "right", padding: "12px"}
    }
  }

  // eslint-disable-next-line no-unused-vars
  const RenderNumber = (props) => {
    let numberStyle = {width:"100%", fontSize: "0.875rem", textAlign: "right"};
    if (props?.column?.colId.startsWith('actual')) {
      if (props.data["booked"] === props.data["actual"]){
        numberStyle = {width:"100%", fontSize: "0.875rem", textAlign: "right", color: 'rgba(0,0,0,0.5)'};
      }
    }
    const suffixes = ["", " Kg", " M3", " Kg"];
    const numberProps = {...props, style: numberStyle, decimalScale: props.rowIndex === 0 ? 0 : props.decimalScale, suffix: suffixes[props.rowIndex] }
    return (
      <MaskedNumber {...numberProps} />
    )
  }

  const numericColumnRenderer = {
    component: "RenderNumber",
    params: {
      isdatagrid: true,
      displayType: "text",
//      value: props.value,
      decimalScale: 2,
      fixedDecimalScale: true,
      style: {width:"100%", fontSize: "0.875rem", textAlign: "right"}
    }
  }

  const isEditable = (params) => {
    return params.node.rowIndex !== 3
  }

  if (props.bookingRecord) {
    const booking = props.bookingRecord;

    columns.push({field: "item", minWidth: 180, maxWidth: 180, editable: false, type: undefined, pinned: true });
    columns.push({field: "booked", headerName: "Booked", editable: false, type: "numericColumn", cellEditor: {...numericColumnEditor}, cellRenderer: {...numericColumnRenderer}});
    columns.push({field: "actual", headerName: "Actual", editable: isEditable, type: "numericColumn", cellEditor: {...numericColumnEditor}, cellRenderer: {...numericColumnRenderer}, onCellValueChanged: handleCellValueChanged});
    columns.push({field: "diff", headerName: "Difference", editable: false, type: "numericColumn", cellEditor: {...numericColumnEditor}, cellRenderer: {...numericColumnRenderer}});

    rows.push({item: "Pieces", booked: booking.expectedPieces || 0, actual: booking.actualPieces || booking.expectedPieces, diff: ((booking.actualPieces ?? booking.expectedPieces) - booking.expectedPieces) || 0});
    rows.push({item: "Weight", booked: booking.expectedWeightKg || 0, actual: booking.actualWeightKg || booking.expectedWeightKg, diff: ((booking.actualWeightKg ?? booking.expectedWeightKg) - booking.expectedWeightKg) || 0});
    rows.push({item: "Volume", booked: booking.expectedVolume || 0, actual: booking.actualVolume || booking.expectedVolume, diff: ((booking.actualVolume ?? booking.expectedVolume) - booking.expectedVolume) || 0});
    rows.push({item: "Chargeable Weight", booked: booking.expectedChargeableWeight || 0, actual: booking.actualChargeableWeight || booking.expectedChargeableWeight, diff: ((booking.actualChargeableWeight  ?? booking.expectedChargeableWeight) - booking.expectedChargeableWeight) || 0});
  }

  function handleClose() {
    props.onChange();
  }

  function handleUpdate() {
    const rows = dataGridRef.current.getAllGridRows();

    if (props.bookingRecord && rows && Array.isArray(rows)) {
      const booking = props.bookingRecord;
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
          switch (i) {
            case 0:
              booking.actualPieces = row.actual;
              break;
            case 1:
              booking.actualWeightKg = row.actual;
              break;
            case 2:
              booking.actualVolume = row.actual;
              break;
            case 3:
              booking.actualChargeableWeight = row.actual;
              break;
          }
      }

      props.onChange(booking);
    }
    else
      props.onChange(null);
  }

  const dataGridRef = useRef();
  return (
    <div id={"FlownValues"}>
      <Dialog
        open={props.open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        disableBackdropClick={true}
        maxWidth={"sm"}
        fullWidth={true}
      >
        <DialogTitle id="form-dialog-title">
          <div style={{clear: "both", backgroundColor:theme.palette.primary.main, padding:"10px"}}>
          {dialogTitle}
          </div>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please enter the actual flown values in the grid fields below.
          </DialogContentText>
          <DataGrid
            ref={dataGridRef}
            columns={columns}
            rows={rows}
            components={{RenderNumber}}
            height={220}
          >
          </DataGrid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleUpdate} color="primary">
            Update Actual Values
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ActualFlownValues;
